/**
 * `api/useMemberMessages.ts` — Message your lead (#2077, D7).
 *
 * The member's side: `useMyMessages` (their own last few on one team) and
 * `useSendMessageToLead`. The lead's side: `useTeamMessages` (the next-steps
 * line's unread count, the People row's latest time, the messages list) and
 * `useMarkMessageRead`.
 *
 * Every read is `retry: false` and degrades to "no messages" on an error: the
 * SPA auto-deploys ahead of the API, and between the two deploys these routes
 * 404 — the drawer line and the People row then simply do not show.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

export type MyMessage = components["schemas"]["MyMessageRead"];
export type TeamMessage = components["schemas"]["TeamMessageRead"];
export type TeamMessagesResponse = components["schemas"]["TeamMessagesResponse"];

/** The body cap — the API answers 422 above it. */
export const MESSAGE_BODY_MAX = 500;

/** How many of their own messages the member's screen shows. */
export const MY_MESSAGES_SHOWN = 3;

export const MY_MESSAGES_KEY = (teamId: string) => ["member-messages", "mine", teamId] as const;
export const TEAM_MESSAGES_KEY = (teamId: string) => ["member-messages", "team", teamId] as const;

export function useMyMessages(teamId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: [...MY_MESSAGES_KEY(teamId ?? ""), accountId],
    enabled: Boolean(teamId && accountId),
    retry: false,
    queryFn: () =>
      apiFetch<{ messages: MyMessage[] }>(
        `/api/account/me/messages?team_id=${teamId}&limit=${MY_MESSAGES_SHOWN}`,
      ),
  });
}

export function useSendMessageToLead() {
  const queryClient = useQueryClient();
  return useMutation<MyMessage, ApiError | Error, { teamId: string; body: string }>({
    mutationFn: ({ teamId, body }) =>
      apiFetch<MyMessage>("/api/account/me/messages", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ team_id: teamId, body }),
      }),
    onSuccess: (_row, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: MY_MESSAGES_KEY(teamId) });
    },
  });
}

/** The team's messages for a lead. `teamId` undefined ⇒ no read at all. */
export function useTeamMessages(
  teamId: string | undefined,
  { membershipId }: { membershipId?: string | undefined } = {},
) {
  const { accountId } = useAuth();
  const filter = membershipId ? `?membership_id=${membershipId}` : "";
  return useQuery({
    queryKey: [...TEAM_MESSAGES_KEY(teamId ?? ""), membershipId ?? "all", accountId],
    enabled: Boolean(teamId && accountId),
    retry: false,
    queryFn: () => apiFetch<TeamMessagesResponse>(`/api/teams/${teamId}/messages${filter}`),
  });
}

export function useMarkMessageRead(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation<TeamMessage, ApiError | Error, string>({
    mutationFn: (messageId) =>
      apiFetch<TeamMessage>(`/api/teams/${teamId}/messages/${messageId}/read`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_MESSAGES_KEY(teamId) });
    },
  });
}

/** "Mon 6 Oct · 14:05" — a message's time, in the viewer's local day. */
export function messageTimeLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const weekday = at.toLocaleDateString("en-GB", { weekday: "short" });
  const month = at.toLocaleDateString("en-GB", { month: "short" });
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${weekday} ${at.getDate()} ${month} · ${time}`;
}

/** "Mon 6 Oct" — the People row's day only. */
export function messageDayLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const weekday = at.toLocaleDateString("en-GB", { weekday: "short" });
  const month = at.toLocaleDateString("en-GB", { month: "short" });
  return `${weekday} ${at.getDate()} ${month}`;
}
